import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';
import { reportKeys } from './useReports';

// ─── Types ──────────────────────────────────────────────────────────────────

export type DataSource =
  | 'contacts'
  | 'jobs'
  | 'billing'
  | 'time_entries'
  | 'documents'
  | 'wip_entries'
  | 'client_ledger'
  | 'capacity'
  | 'audit_logs'
  | 'users';

export type FilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equal'
  | 'less_than_or_equal'
  | 'in'
  | 'not_in'
  | 'is_null'
  | 'is_not_null'
  | 'between';

export type AggregationType = 'sum' | 'avg' | 'count' | 'min' | 'max';

export type SortDirection = 'asc' | 'desc';

export type CalculatedFieldOperation = 'add' | 'subtract' | 'multiply' | 'divide' | 'percentage';

export interface FieldDefinition {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'boolean';
  source: DataSource;
  category: string;
  lookupCategory?: string | null;
  staticOptions?: { value: string; label: string }[];
}

export interface ReportFilter {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string | number | string[] | null;
}

export interface GroupByConfig {
  field: string;
  aggregations: AggregationConfig[];
}

export interface AggregationConfig {
  field: string;
  type: AggregationType;
  alias?: string;
}

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

export interface CalculatedField {
  id: string;
  name: string;
  label: string;
  operation: CalculatedFieldOperation;
  fieldA: string;
  fieldB: string;
  /** Present when loaded from a saved report (API shape). */
  alias?: string;
  expression?: string;
}

export type CalculatedFieldPayload =
  | Pick<CalculatedField, 'name' | 'label' | 'operation' | 'fieldA' | 'fieldB'>
  | { alias: string; expression: string };

export interface ReportBuilderJoin {
  source: DataSource;
  type: 'left' | 'inner';
  from_column?: string;
  to_column?: string;
  label?: string;
}

export interface ReportBuilderQuery {
  source: DataSource;
  joins?: ReportBuilderJoin[];
  fields: string[];
  filters: Omit<ReportFilter, 'id'>[];
  group_by?: string[];
  aggregations?: AggregationConfig[];
  calculated_fields?: CalculatedFieldPayload[];
  sort?: SortConfig[];
  include_tags?: boolean;
  include_dynamic_fields?: boolean;
  limit?: number;
  data_rule_ids?: string[];
}

export interface ReportBuilderResult {
  columns: { id: string; label: string; format?: 'decimal' }[];
  rows: Record<string, string | number | boolean | null>[];
  total: number;
  executionTimeMs: number;
}

export interface SavedBuilderReport {
  id: string;
  name: string;
  description?: string;
  query: ReportBuilderQuery;
  sharedWith?: string[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Maps snake_case saved_reports API rows to SavedBuilderReport. */
export function mapSavedBuilderReport(r: Record<string, unknown>): SavedBuilderReport {
  const config =
    typeof r.config === 'string'
      ? (JSON.parse(r.config) as Record<string, unknown>)
      : ((r.config as Record<string, unknown> | undefined) ?? {});

  const rewrite = (value: string) =>
    value.replace(/\binvoices\./g, 'billing.').replace(/\bwip\./g, 'wip_entries.');

  const query = { ...((r.query ?? config.query) as ReportBuilderQuery) };
  if ((query.source as string) === 'invoices') query.source = 'billing';
  if ((query.source as string) === 'wip') query.source = 'wip_entries';
  if (query.fields) query.fields = query.fields.map(rewrite);
  if (query.group_by) query.group_by = query.group_by.map(rewrite);
  if (query.filters) {
    query.filters = query.filters.map((filter) => ({ ...filter, field: rewrite(filter.field) }));
  }
  if (query.aggregations) {
    query.aggregations = query.aggregations.map((agg) => ({ ...agg, field: rewrite(agg.field) }));
  }
  if (query.sort) {
    query.sort = query.sort.map((entry) => ({ ...entry, field: rewrite(entry.field) }));
  }
  if (query.joins) {
    query.joins = query.joins.map((join) => ({
      ...join,
      source:
        (join.source as string) === 'invoices'
          ? 'billing'
          : (join.source as string) === 'wip'
            ? 'wip_entries'
            : join.source,
    }));
  }

  return {
    id: r.id as string,
    name: r.name as string,
    description: r.description as string | undefined,
    query,
    sharedWith: (r.shared_with ?? r.sharedWith) as string[] | undefined,
    createdBy: (r.user_id ?? r.createdBy ?? '') as string,
    createdAt: (r.created_at ?? r.createdAt) as string,
    updatedAt: (r.updated_at ?? r.updatedAt) as string,
  };
}

// ─── Data Source Field Definitions ──────────────────────────────────────────

export const MAX_REPORT_JOINS = 4;
export const MAX_REPORT_FIELDS = 50;

export const DATA_SOURCE_OPTIONS: { value: DataSource; label: string; description: string }[] = [
  { value: 'contacts', label: 'Contacts', description: 'Client and contact records' },
  { value: 'jobs', label: 'Jobs', description: 'Job engagements and workflow data' },
  {
    value: 'billing',
    label: 'Billing',
    description: 'Invoices, quotes, credits, and related documents',
  },
  { value: 'time_entries', label: 'Time Entries', description: 'Time tracking and timesheets' },
  { value: 'documents', label: 'Documents', description: 'Document vault records' },
  { value: 'wip_entries', label: 'WIP', description: 'Work in progress ledger' },
  { value: 'client_ledger', label: 'Client Ledger', description: 'Client account ledger entries' },
  { value: 'capacity', label: 'Capacity', description: 'Staff availability and utilization' },
  {
    value: 'users',
    label: 'Users',
    description: 'Staff accounts with roles, permissions, and teams',
  },
  {
    value: 'audit_logs',
    label: 'Audit / Logs',
    description: 'Audit trail and activity history across the system',
  },
];

function field(
  source: DataSource,
  name: string,
  label: string,
  type: FieldDefinition['type'],
  category: string,
  extra?: Partial<FieldDefinition>,
): FieldDefinition {
  return { id: `${source}.${name}`, name, label, type, source, category, ...extra };
}

export const SOURCE_FIELDS: Record<DataSource, FieldDefinition[]> = {
  contacts: [
    field('contacts', 'first_name', 'First Name', 'text', 'Basic Info'),
    field('contacts', 'last_name', 'Last Name', 'text', 'Basic Info'),
    field('contacts', 'email', 'Email', 'text', 'Basic Info'),
    field('contacts', 'phone', 'Phone', 'text', 'Basic Info'),
    field('contacts', 'secondary_phone', 'Secondary Phone', 'text', 'Basic Info'),
    field('contacts', 'business_name', 'Business Name', 'text', 'Basic Info'),
    field('contacts', 'business_number', 'Business Number', 'text', 'Basic Info'),
    field('contacts', 'client_code', 'Client Code', 'text', 'Basic Info'),
    field('contacts', 'mailing_name', 'Mailing Name', 'text', 'Basic Info'),
    field('contacts', 'salutation', 'Salutation', 'text', 'Basic Info'),
    field('contacts', 'contact_type', 'Contact Type', 'text', 'Classification'),
    field('contacts', 'contact_nature', 'Contact Nature', 'text', 'Classification'),
    field('contacts', 'contact_category', 'Contact Category', 'text', 'Classification'),
    field('contacts', 'status', 'Status', 'text', 'Classification'),
    field('contacts', 'delivery_method', 'Delivery Method', 'text', 'Classification'),
    field('contacts', 'lead_source', 'Lead Source', 'text', 'Lead'),
    field('contacts', 'lead_stage', 'Lead Stage', 'text', 'Lead'),
    field('contacts', 'lead_stage_changed_at', 'Lead Stage Changed', 'date', 'Lead'),
    field('contacts', 'converted_at', 'Converted At', 'date', 'Lead'),
    field('contacts', 'portal_invitation_status', 'Portal Invitation Status', 'text', 'Portal'),
    field('contacts', 'address_line1', 'Address Line 1', 'text', 'Address'),
    field('contacts', 'address_line2', 'Address Line 2', 'text', 'Address'),
    field('contacts', 'city', 'City', 'text', 'Address'),
    field('contacts', 'province', 'Province', 'text', 'Address'),
    field('contacts', 'postal_code', 'Postal Code', 'text', 'Address'),
    field('contacts', 'country', 'Country', 'text', 'Address'),
    field('contacts', 'date_of_birth', 'Date of Birth', 'date', 'Dates'),
    field('contacts', 'created_at', 'Created Date', 'date', 'Dates'),
    field('contacts', 'updated_at', 'Updated Date', 'date', 'Dates'),
  ],
  jobs: [
    field('jobs', 'title', 'Job Title', 'text', 'Basic Info'),
    field('jobs', 'job_code', 'Job Code', 'text', 'Basic Info'),
    field('jobs', 'description', 'Description', 'text', 'Basic Info'),
    field('jobs', 'job_type', 'Job Type', 'text', 'Classification'),
    field('jobs', 'status', 'Status', 'text', 'Classification'),
    field('jobs', 'priority', 'Priority', 'text', 'Classification'),
    field('jobs', 'budget_amount', 'Budget Amount', 'number', 'Financial'),
    field('jobs', 'estimated_hours', 'Estimated Hours', 'number', 'Time'),
    field('jobs', 'progress_percentage', 'Progress %', 'number', 'Status'),
    field('jobs', 'is_overdue', 'Is Overdue', 'boolean', 'Status'),
    field('jobs', 'is_recurring', 'Is Recurring', 'boolean', 'Status'),
    field('jobs', 'recurrence_frequency', 'Recurrence Frequency', 'text', 'Status'),
    field('jobs', 'start_date', 'Start Date', 'date', 'Dates'),
    field('jobs', 'due_date', 'Due Date', 'date', 'Dates'),
    field('jobs', 'completed_date', 'Completed Date', 'date', 'Dates'),
    field('jobs', 'cancellation_reason', 'Cancellation Reason', 'text', 'Status'),
    field('jobs', 'created_at', 'Created Date', 'date', 'Dates'),
    field('jobs', 'updated_at', 'Updated Date', 'date', 'Dates'),
  ],
  billing: [
    field('billing', 'billing_type', 'Billing Type', 'text', 'Classification', {
      staticOptions: [
        { value: 'invoice', label: 'Invoice' },
        { value: 'quote', label: 'Quote' },
        { value: 'credit_note', label: 'Credit Note' },
        { value: 'debit_note', label: 'Debit Note' },
        { value: 'advance', label: 'Advance' },
        { value: 'write_off', label: 'Write Off' },
        { value: 'discount', label: 'Discount' },
      ],
    }),
    field('billing', 'document_number', 'Document Number', 'text', 'Basic Info'),
    field('billing', 'status', 'Status', 'text', 'Classification'),
    field('billing', 'service_type_code', 'Service Type', 'text', 'Classification'),
    field('billing', 'job_reference', 'Job Reference', 'text', 'Basic Info'),
    field('billing', 'notes', 'Notes', 'text', 'Basic Info'),
    field('billing', 'billing_entity_name', 'Billing Entity', 'text', 'Address'),
    field('billing', 'billing_address', 'Billing Address', 'text', 'Address'),
    field('billing', 'client_address', 'Client Address', 'text', 'Address'),
    field('billing', 'subtotal', 'Subtotal', 'number', 'Amounts'),
    field('billing', 'tax_amount', 'Tax Amount', 'number', 'Amounts'),
    field('billing', 'rounding_amount', 'Rounding', 'number', 'Amounts'),
    field('billing', 'total', 'Total', 'number', 'Amounts'),
    field('billing', 'total_receivable', 'Total Receivable', 'number', 'Amounts'),
    field('billing', 'amount_paid', 'Amount Paid', 'number', 'Amounts'),
    field('billing', 'outstanding_balance', 'Balance Due', 'number', 'Amounts'),
    field('billing', 'payment_terms_days', 'Payment Terms (Days)', 'number', 'Terms'),
    field('billing', 'is_recurring', 'Is Recurring', 'boolean', 'Classification'),
    field('billing', 'document_date', 'Document Date', 'date', 'Dates'),
    field('billing', 'due_date', 'Due Date', 'date', 'Dates'),
    field('billing', 'sent_at', 'Sent At', 'date', 'Dates'),
    field('billing', 'viewed_at', 'Viewed At', 'date', 'Dates'),
    field('billing', 'paid_at', 'Paid At', 'date', 'Dates'),
    field('billing', 'voided_at', 'Voided At', 'date', 'Dates'),
    field('billing', 'created_at', 'Created Date', 'date', 'Dates'),
    field('billing', 'updated_at', 'Updated Date', 'date', 'Dates'),
  ],
  time_entries: [
    field('time_entries', 'description', 'Description', 'text', 'Basic Info'),
    field('time_entries', 'duration_minutes', 'Duration (Minutes)', 'number', 'Time'),
    field('time_entries', 'hourly_rate', 'Hourly Rate', 'number', 'Financial'),
    field('time_entries', 'is_billable', 'Is Billable', 'boolean', 'Classification'),
    field('time_entries', 'approval_status', 'Approval Status', 'text', 'Classification'),
    field('time_entries', 'rejection_comment', 'Rejection Comment', 'text', 'Classification'),
    field('time_entries', 'entry_date', 'Entry Date', 'date', 'Dates'),
    field('time_entries', 'created_at', 'Created Date', 'date', 'Dates'),
    field('time_entries', 'updated_at', 'Updated Date', 'date', 'Dates'),
  ],
  documents: [
    field('documents', 'file_name', 'Filename', 'text', 'Basic Info'),
    field('documents', 'folder_name', 'Folder Name', 'text', 'Basic Info'),
    field('documents', 'file_type', 'File Type', 'text', 'Classification'),
    field('documents', 'category', 'Category', 'text', 'Classification'),
    field('documents', 'description', 'Description', 'text', 'Basic Info'),
    field('documents', 'file_size_bytes', 'File Size (Bytes)', 'number', 'Details'),
    field('documents', 'version', 'Version', 'number', 'Details'),
    field('documents', 'is_portal_visible', 'Portal Visible', 'boolean', 'Status'),
    field('documents', 'malware_scan_status', 'Malware Scan Status', 'text', 'Status'),
    field('documents', 'is_locked', 'Is Locked', 'boolean', 'Status'),
    field('documents', 'created_at', 'Upload Date', 'date', 'Dates'),
  ],
  wip_entries: [
    field('wip_entries', 'job_title', 'Job', 'text', 'Job'),
    field('wip_entries', 'job_code', 'Job Code', 'text', 'Job'),
    field('wip_entries', 'client_name', 'Client Name', 'text', 'Client'),
    field('wip_entries', 'client_code', 'Client Code', 'text', 'Client'),
    field('wip_entries', 'staff_name', 'Staff Name', 'text', 'Staff'),
    field('wip_entries', 'entry_type', 'Entry Type', 'text', 'Classification', {
      staticOptions: [
        { value: 'time', label: 'Time' },
        { value: 'write_up', label: 'Write-Up' },
        { value: 'write_down', label: 'Write-Down' },
        { value: 'invoice', label: 'Invoice' },
        { value: 'reversal', label: 'Reversal' },
        { value: 'credit_note', label: 'Credit Note' },
        { value: 'write_off', label: 'Write-Off' },
        { value: 'discount', label: 'Discount' },
        { value: 'advance', label: 'Advance' },
      ],
    }),
    field('wip_entries', 'invoice_number', 'Invoice Number', 'text', 'Basic Info'),
    field('wip_entries', 'description', 'Description', 'text', 'Basic Info'),
    field('wip_entries', 'hours', 'Hours', 'number', 'Time'),
    field('wip_entries', 'rate', 'Rate', 'number', 'Financial'),
    field('wip_entries', 'amount', 'Amount', 'number', 'Ledger'),
    field('wip_entries', 'debit', 'Debit', 'number', 'Ledger'),
    field('wip_entries', 'credit', 'Credit', 'number', 'Ledger'),
    field('wip_entries', 'balance', 'Balance', 'number', 'Ledger'),
    field('wip_entries', 'created_at', 'Created Date', 'date', 'Dates'),
  ],
  client_ledger: [
    field('client_ledger', 'client_name', 'Client Name', 'text', 'Client'),
    field('client_ledger', 'client_code', 'Client Code', 'text', 'Client'),
    field('client_ledger', 'business_name', 'Business Name', 'text', 'Client'),
    field('client_ledger', 'client_email', 'Client Email', 'text', 'Client'),
    field('client_ledger', 'entry_type', 'Entry Type', 'text', 'Classification', {
      staticOptions: [
        { value: 'invoice', label: 'Invoice' },
        { value: 'payment', label: 'Payment' },
        { value: 'credit_note', label: 'Credit Note' },
        { value: 'debit_note', label: 'Debit Note' },
        { value: 'adjustment', label: 'Adjustment' },
        { value: 'write_off', label: 'Write Off' },
        { value: 'discount', label: 'Discount' },
        { value: 'advance', label: 'Advance' },
      ],
    }),
    field('client_ledger', 'reference_type', 'Reference Type', 'text', 'Classification'),
    field('client_ledger', 'reference_number', 'Reference Number', 'text', 'Basic Info'),
    field('client_ledger', 'description', 'Description', 'text', 'Basic Info'),
    field('client_ledger', 'amount', 'Amount', 'number', 'Ledger'),
    field('client_ledger', 'debit', 'Debit', 'number', 'Ledger'),
    field('client_ledger', 'credit', 'Credit', 'number', 'Ledger'),
    field('client_ledger', 'balance', 'Balance', 'number', 'Ledger'),
    field('client_ledger', 'entry_date', 'Entry Date', 'date', 'Dates'),
    field('client_ledger', 'created_at', 'Created Date', 'date', 'Dates'),
  ],
  capacity: [
    field('capacity', 'user_name', 'Staff Name', 'text', 'Staff'),
    field('capacity', 'user_email', 'Staff Email', 'text', 'Staff'),
    field('capacity', 'job_role', 'Job Role', 'text', 'Staff'),
    field('capacity', 'department', 'Department', 'text', 'Staff'),
    field('capacity', 'user_status', 'Staff Status', 'text', 'Staff'),
    field('capacity', 'date', 'Week Starting', 'date', 'Availability'),
    field('capacity', 'available_hours', 'Available Hours', 'number', 'Availability'),
    field('capacity', 'assigned_hours', 'Assigned Hours', 'number', 'Availability'),
    field('capacity', 'utilization_percent', 'Utilization %', 'number', 'Availability'),
    field('capacity', 'absence_type', 'Absence Type', 'text', 'Availability'),
    field('capacity', 'notes', 'Notes', 'text', 'Availability'),
    field('capacity', 'hourly_rate', 'Hourly Rate', 'number', 'Financial'),
    field('capacity', 'created_at', 'Created Date', 'date', 'Dates'),
    field('capacity', 'updated_at', 'Updated Date', 'date', 'Dates'),
  ],
  users: [
    field('users', 'name', 'Name', 'text', 'Account'),
    field('users', 'email', 'Email', 'text', 'Account'),
    field('users', 'status', 'Status', 'text', 'Account'),
    field('users', 'job_role', 'Job Role', 'text', 'Account'),
    field('users', 'department', 'Department', 'text', 'Account'),
    field('users', 'licence_type', 'Licence Type', 'text', 'Account'),
    field('users', 'mfa_enabled', 'MFA Enabled', 'boolean', 'Account'),
    field('users', 'last_login_at', 'Last Login', 'date', 'Account'),
    field('users', 'created_at', 'Created Date', 'date', 'Account'),
    field('users', 'primary_role', 'Primary Role', 'text', 'Access'),
    field('users', 'roles', 'Roles', 'text', 'Access'),
    field('users', 'data_scope', 'Data Scope', 'text', 'Access'),
    field('users', 'permissions', 'Permissions', 'text', 'Access'),
    field('users', 'teams', 'Teams', 'text', 'Teams'),
  ],
  audit_logs: [
    field('audit_logs', 'action', 'Action', 'text', 'Activity', {
      staticOptions: [
        { value: 'create', label: 'Create' },
        { value: 'update', label: 'Update' },
        { value: 'delete', label: 'Delete' },
        { value: 'view', label: 'View' },
        { value: 'download', label: 'Download' },
      ],
    }),
    field('audit_logs', 'entity_type', 'Entity Type', 'text', 'Activity'),
    field('audit_logs', 'entity_id', 'Entity ID', 'text', 'Activity'),
    field('audit_logs', 'user_id', 'Performed By (User ID)', 'text', 'Actor'),
    field('audit_logs', 'user_type', 'Actor Type', 'text', 'Actor'),
    field('audit_logs', 'ip_address', 'IP Address', 'text', 'Context'),
    field('audit_logs', 'user_agent', 'User Agent', 'text', 'Context'),
    field('audit_logs', 'metadata', 'Metadata', 'text', 'Details'),
    field('audit_logs', 'previous_value', 'Previous Value', 'text', 'Details'),
    field('audit_logs', 'new_value', 'New Value', 'text', 'Details'),
    field('audit_logs', 'created_at', 'Timestamp', 'date', 'Dates'),
  ],
};

const BUILDER_COLUMN_LABELS = new Map<string, string>(
  Object.values(SOURCE_FIELDS).flatMap((fields) =>
    fields.flatMap((f) => [
      [f.id, f.label],
      [f.id.replace('.', '_'), f.label],
    ]),
  ),
);

export function builderColumnLabel(columnId: string, fallback?: string): string {
  return (
    BUILDER_COLUMN_LABELS.get(columnId) ??
    fallback ??
    columnId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export const FILTER_OPERATORS: { value: FilterOperator; label: string; types: string[] }[] = [
  { value: 'equals', label: 'Equals', types: ['text', 'number', 'date', 'boolean'] },
  { value: 'not_equals', label: 'Not Equals', types: ['text', 'number', 'date', 'boolean'] },
  { value: 'contains', label: 'Contains', types: ['text'] },
  { value: 'not_contains', label: 'Does Not Contain', types: ['text'] },
  { value: 'greater_than', label: 'Greater Than', types: ['number', 'date'] },
  { value: 'less_than', label: 'Less Than', types: ['number', 'date'] },
  { value: 'greater_than_or_equal', label: 'Greater or Equal', types: ['number', 'date'] },
  { value: 'less_than_or_equal', label: 'Less or Equal', types: ['number', 'date'] },
  { value: 'in', label: 'In (comma-separated)', types: ['text'] },
  { value: 'is_null', label: 'Is Empty', types: ['text', 'number', 'date'] },
  { value: 'is_not_null', label: 'Is Not Empty', types: ['text', 'number', 'date'] },
  { value: 'between', label: 'Between', types: ['number', 'date'] },
];

// ─── Query Keys ─────────────────────────────────────────────────────────────

export const reportBuilderKeys = {
  all: ['report-builder'] as const,
  preview: (query: ReportBuilderQuery) => [...reportBuilderKeys.all, 'preview', query] as const,
  saved: () => [...reportBuilderKeys.all, 'saved'] as const,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * Execute a dynamic report builder query.
 * Requirements: 24.19
 */
export function useReportBuilderPreview() {
  return useMutation({
    mutationFn: async (query: ReportBuilderQuery) => {
      const response = await apiClient.post<ApiResponse<ReportBuilderResult>>(
        '/reports/builder',
        query,
      );
      const result = response.data.data;
      return {
        ...result,
        columns: (result.columns ?? []).map((col) => ({
          ...col,
          label: builderColumnLabel(col.id, col.label),
        })),
      };
    },
  });
}

/**
 * Save a report builder configuration.
 * Requirements: 24.11, 24.19
 */
export function useSaveBuilderReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      query: ReportBuilderQuery;
      sharedWith?: string[];
    }) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        '/reports/saved',
        payload,
      );
      return mapSavedBuilderReport(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportBuilderKeys.saved() });
      queryClient.invalidateQueries({ queryKey: reportKeys.saved() });
    },
  });
}

/**
 * Update an existing saved report configuration.
 */
export function useUpdateBuilderReport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: { name: string; query: ReportBuilderQuery };
    }) => {
      const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
        `/reports/saved/${id}`,
        payload,
      );
      return mapSavedBuilderReport(response.data.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reportBuilderKeys.saved() });
    },
  });
}

/**
 * Fetch saved builder reports.
 * Requirements: 24.11
 */
export function useSavedBuilderReports() {
  return useQuery({
    queryKey: reportBuilderKeys.saved(),
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/reports/saved');
      return (response.data.data ?? []).map(mapSavedBuilderReport);
    },
  });
}

// ─── Data Rule Presets ──────────────────────────────────────────────────────

export interface ReportingDataRule {
  id: string;
  name: string;
  description: string | null;
  field: string;
  operator: string;
  values: string[];
  conditions: { field: string; operator: string; values: string[] }[];
  logic: 'and' | 'or';
  entities: string[];
}

/**
 * Fetch data rules assigned to the 'reporting' module.
 * These can be applied as filter presets in the report builder.
 */
export function useReportingDataRules() {
  return useQuery({
    queryKey: [...reportBuilderKeys.all, 'data-rules'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<ReportingDataRule[]>>(
        '/reports/builder/data-rules',
      );
      return response.data.data;
    },
  });
}

// ─── Builder Metadata ───────────────────────────────────────────────────────

export interface JoinPath {
  target: DataSource;
  from_column: string;
  to_column: string;
  label: string;
}

export interface DynamicFieldDef {
  id: string;
  entityType: string;
  label: string;
  fieldType: string;
  isRequired: boolean;
  source: 'dynamic_field_definitions';
}

export interface FormFieldDef {
  id: string;
  fieldKey: string;
  label: string;
  fieldType: string;
  isRequired: boolean;
  isSystemField: boolean;
  entityScope: string;
  formName: string;
  section: string;
  dbColumn: string | null;
  lookupCategory: string | null;
  options: { value: string; label: string }[];
  source: 'form_fields';
}

export interface TagDef {
  id: string;
  name: string;
  code: string;
  color: string | null;
}

export interface BuilderMetadata {
  joinPaths: Record<string, JoinPath[]>;
  dynamicFields: DynamicFieldDef[];
  formFields: FormFieldDef[];
  tags: TagDef[];
  sourceColumns: Record<string, string[]>;
}

/**
 * Fetch report builder metadata: join paths, dynamic fields, tags, source columns.
 */
export function useBuilderMetadata() {
  return useQuery({
    queryKey: [...reportBuilderKeys.all, 'metadata'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<BuilderMetadata>>(
        '/reports/builder/metadata',
      );
      return response.data.data;
    },
  });
}
